import {
  collection,
  getDoc,
  getDocs,
  limit,
  query,
  where,
  type Transaction,
} from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { publicCollectionDoc, publicDirectoryDoc } from '../../../firebase/collections';

export interface MemberProfileFields {
  displayName: string;
  slug: string;
  avatarUrl: string | null;
}

function profileFromDirectoryData(
  data: Record<string, unknown>,
  fallbackSlug: string
): MemberProfileFields {
  const slug =
    (typeof data.slug === 'string' && data.slug) || fallbackSlug;
  return {
    displayName:
      (typeof data.displayName === 'string' && data.displayName.trim()) || slug,
    slug,
    avatarUrl:
      (typeof data.avatarUrl === 'string' && data.avatarUrl) ||
      (typeof data.photoURL === 'string' && data.photoURL) ||
      null,
  };
}

/**
 * Resolve a player's public profile by uid.
 * Uses world-readable `publicCollections/{uid}` → `publicDirectory/{slug}`,
 * then falls back to a uid query on `publicDirectory`.
 * (private `users/{uid}` is owner-only and cannot be used for other players).
 */
export async function readPublicPlayerByUid(
  uid: string
): Promise<MemberProfileFields | null> {
  try {
    const gateSnap = await getDoc(publicCollectionDoc(uid));
    if (gateSnap.exists()) {
      const slug = gateSnap.data()?.slug;
      if (typeof slug === 'string' && slug) {
        const dirSnap = await getDoc(publicDirectoryDoc(slug));
        if (!dirSnap.exists()) {
          return { displayName: slug, slug, avatarUrl: null };
        }
        return profileFromDirectoryData(dirSnap.data() as Record<string, unknown>, slug);
      }
    }

    // Legacy / partial sync: directory exists but publicCollections gate is missing.
    const q = query(
      collection(db, 'publicDirectory'),
      where('uid', '==', uid),
      limit(1)
    );
    const snap = await getDocs(q);
    const first = snap.docs[0];
    if (!first) return null;
    return profileFromDirectoryData(first.data() as Record<string, unknown>, first.id);
  } catch {
    return null;
  }
}

/**
 * Resolve denormalized member profile fields inside a transaction.
 * Prefers publicDirectory by slug from the user doc; falls back to user fields.
 */
export async function readMemberProfile(
  transaction: Transaction,
  uid: string,
  userData: Record<string, unknown> | undefined,
  authDisplayName?: string | null
): Promise<MemberProfileFields> {
  const fallbackName =
    (typeof authDisplayName === 'string' && authDisplayName.trim()) ||
    (typeof userData?.displayName === 'string' && userData.displayName.trim()) ||
    'Player';
  const fallbackSlug =
    (typeof userData?.slug === 'string' && userData.slug) || uid.slice(0, 6).toLowerCase();
  const fallbackAvatar =
    (typeof userData?.avatarUrl === 'string' && userData.avatarUrl) ||
    (typeof userData?.photoURL === 'string' && userData.photoURL) ||
    null;

  const slug = typeof userData?.slug === 'string' ? userData.slug : null;
  if (!slug) {
    return { displayName: fallbackName, slug: fallbackSlug, avatarUrl: fallbackAvatar };
  }

  const dirSnap = await transaction.get(publicDirectoryDoc(slug));
  if (!dirSnap.exists()) {
    return { displayName: fallbackName, slug: fallbackSlug, avatarUrl: fallbackAvatar };
  }

  const data = dirSnap.data();
  return {
    displayName:
      (typeof data.displayName === 'string' && data.displayName.trim()) || fallbackName,
    slug: (typeof data.slug === 'string' && data.slug) || fallbackSlug,
    avatarUrl:
      (typeof data.avatarUrl === 'string' && data.avatarUrl) ||
      (typeof data.photoURL === 'string' && data.photoURL) ||
      fallbackAvatar,
  };
}

/** Build profile fields from a signed-in user's own doc + optional public lookup. */
export function profileFromUserData(
  uid: string,
  userData: Record<string, unknown> | undefined,
  publicProfile?: MemberProfileFields | null,
  authDisplayName?: string | null,
  authPhotoURL?: string | null
): MemberProfileFields {
  const displayName =
    publicProfile?.displayName ||
    (typeof userData?.displayName === 'string' && userData.displayName.trim()) ||
    (typeof authDisplayName === 'string' && authDisplayName.trim()) ||
    'Player';
  const slug =
    publicProfile?.slug ||
    (typeof userData?.slug === 'string' && userData.slug) ||
    uid.slice(0, 6).toLowerCase();
  const avatarUrl =
    publicProfile?.avatarUrl ??
    (typeof userData?.avatarUrl === 'string' ? userData.avatarUrl : null) ??
    (typeof userData?.photoURL === 'string' ? userData.photoURL : null) ??
    (typeof authPhotoURL === 'string' ? authPhotoURL : null);

  return { displayName, slug, avatarUrl };
}

/** Non-transaction read helper for invite enrichment. */
export async function readUserSlug(uid: string): Promise<string | null> {
  const profile = await readPublicPlayerByUid(uid);
  return profile?.slug ?? null;
}
